// src/components/SearchPane.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete
} from "@mui/material";
import { FaSearch } from "react-icons/fa";
import axios from "axios";

const LL_AUTH_HEADERS = () => ({
  Authorization: `Bearer ${localStorage.getItem("labour_law_token") || ""}`,
});

const currentYear = new Date().getFullYear();
// helper to generate years array, descending
const makeYearOptions = (start = 1950, end = currentYear) => {
  const arr = [];
  for (let y = end; y >= start; y--) arr.push(y);
  return arr;
};



const SearchPane = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [domainList, setDomainList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  // suggestions/autocomplete
  const [options, setOptions] = useState([]); // array of {id,label,title,snippet}
  const [openSuggestions, setOpenSuggestions] = useState(false);
  const suggestTimer = useRef(null);
  const isBareCode = (s3Key) =>
    s3Key && s3Key.includes("/BARE_CODE/");

  const [filters, setFilters] = useState({
    act: "All", // EPF Act, ESI Act, etc.
    state: "", // Used only for State or Central+State
    categories: "All",
    actYear: "All", // new: year filter for the Act (All or a number)
    archive: "All" // new: All / LIVE / ARCHIVED
  });
  const [searchEventId, setSearchEventId] = useState(null);

  // convenience: build params object based on filters
  const buildFilterParams = (f) => {
    const p = {};
    if (f.act && f.act !== "All") p.act_name = f.act;
    if (f.state) p.state = f.state;
    if (f.categories && f.categories !== "All") p.category = f.categories;
    return p;
  };

  const search = async (overrideQuery) => {
    const query = typeof overrideQuery === "string" ? overrideQuery : q;
    // allow search if any filter is set OR query has content
    const hasAnyFilter =
      (filters.actLevel && filters.actLevel !== "All") ||
      (filters.act && filters.act !== "All") ||
      (filters.state && filters.state !== "") ||
      (filters.actYear && filters.actYear !== "All") ||
      (filters.archive && filters.archive !== "All");

    if (!query.trim() && !hasAnyFilter) return; // nothing to search

    setLoading(true);

    try {
      const params = new URLSearchParams({
        ...(query.trim() ? { q: query.trim() } : {}),
        ...buildFilterParams(filters)
      });

      const response = await axios.get(
        //`http://127.0.0.1:8000/docsearch/search_nodes/?${params.toString()}`,
        `http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com/docsearch/search_nodes/?${params.toString()}`,
        { headers: { "Content-Type": "application/json" , ...LL_AUTH_HEADERS() } }
      );

      const data = response.data;

      // ⭐ CAPTURE SEARCH EVENT ID
      if (data?.search_event_id) {
        setSearchEventId(data.search_event_id);
      } else {
        setSearchEventId(null);
      }

      // your backend returns { type: "hybrid", results: [...] } per views.py
      const normalizedResults = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : [];

      setResults(normalizedResults);
    } catch (err) {
      console.error("Search error:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    async function fetchDomains() {
      try {
        const resp = await axios.get(
          //`http://127.0.0.1:8000/docmgmt/list_domains/`
          `http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com/docmgmt/list_domains/`, 
          { headers: LL_AUTH_HEADERS() }
        );
        setDomainList(Array.isArray(resp.data) ? resp.data : resp.data?.domains || []);
      } catch (e) {
        console.error("Domain fetch error", e);
      }
    }
    fetchDomains();
  }, []);

  useEffect(() => {
    async function fetchStates() {
      try {
        const resp = await axios.get(
          //`http://127.0.0.1:8000/docmgmt/list_states/`
          `http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com/docmgmt/list_states/`,
          { headers: LL_AUTH_HEADERS() }
        );
        setStateList(Array.isArray(resp.data) ? resp.data : resp.data?.states || []);
      } catch (e) {
        console.error("State fetch error", e);
      }
    }
    fetchStates();
  }, []);
  useEffect(() => {
    async function fetchCategories() {
      try {
        const resp = await axios.get(
          //`http://127.0.0.1:8000/docmgmt/list_categories/`
          `http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com/docmgmt/list_categories/`,
          { headers: LL_AUTH_HEADERS() }
        );
        setCategoriesList(Array.isArray(resp.data) ? resp.data : resp.data?.categories || []);
      } catch (e) {
        console.error("Category fetch error", e);
      }
    }
    fetchCategories();
  }, []);

  // Debounced suggestions fetch (only when user types)
  useEffect(() => {
    // cleanup previous timer
    if (suggestTimer.current) {
      clearTimeout(suggestTimer.current);
      suggestTimer.current = null;
    }

    const qTrim = (q || "").trim();
    // only when length >= 4 OR when filters (act/actYear/archive) changed and user might want suggestions
    if (qTrim.length >= 4) {
      suggestTimer.current = setTimeout(async () => {
        try {
          const params = new URLSearchParams({
            q: qTrim,
            ...buildFilterParams(filters)
          });

          const resp = await axios.get(
            //`http://127.0.0.1:8000/docsearch/suggest_nodes/?${params.toString()}`
            `http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com/docsearch/suggest_nodes/?${params.toString()}`,
            { headers: LL_AUTH_HEADERS() }
          );
          // expected { suggestions: [ {id,label,title,snippet} ] }
          const s = resp.data?.suggestions || [];
          setOptions(s);
          setOpenSuggestions(true);
        } catch (e) {
          // silently ignore suggestion errors
          console.warn("Suggest error:", e);
          setOptions([]);
          setOpenSuggestions(false);
        }
      }, 300); // 300ms debounce
    } else {
      // If user cleared query but filters are set, optionally fetch light suggestions
      setOptions([]);
      setOpenSuggestions(false);
    }

    return () => {
      if (suggestTimer.current) {
        clearTimeout(suggestTimer.current);
        suggestTimer.current = null;
      }
    };
    // include new filters so suggestions change when actYear/archive toggled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, filters.actLevel, filters.act, filters.state, filters.actYear, filters.archive]);

  const onResultClick = async (item) => {

    try {
      if (searchEventId) {
        axios.post(
          //"http://127.0.0.1:8000/docsearch/log_click/",
          "http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com/docsearch/log_click/",
          {
            search_event_id: searchEventId,
            document_id: item.id,
            document_type: "LLNode",
            state_code: item.state || null,
            domain_code: item.domain || null,
            category_code: item.category || null,
          },
          { headers: LL_AUTH_HEADERS() }
        );
        // ⚠️ intentionally NOT awaited
      }
    } catch (e) {
      console.warn("SearchClickLL logging failed", e);
    }
        // 🔹 BARE_CODE → deferred resolution
    if (isBareCode(item.s3_key)) {
      try {
        const res = await axios.get(
          //`http://127.0.0.1:8000/docsearch/open_node/?node_id=${item.id}`
          `http://ec2-43-205-136-189.ap-south-1.compute.amazonaws.com/docsearch/open_node/?node_id=${item.id}`,
          { headers: LL_AUTH_HEADERS() }
        );
        window.open(res.data.file_url, "_blank");
      } catch (err) {
        console.error("Failed to open bare code:", err);
      }
      return;
    }

    // 🔹 All other documents → direct open
    window.open(item.file_url, "_blank");
  };

  // handle Enter key on input to run search
  const handleKeyDown = (ev) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      search(q);
      setOpenSuggestions(false);
    }
  };

  // generate year list for the Act Year select
  const yearOptions = ["All", ...makeYearOptions(1950, currentYear)];

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={1} alignItems="center">
          {/* Search input with Autocomplete */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              freeSolo
              open={openSuggestions && options.length > 0}
              onOpen={() => setOpenSuggestions(true)}
              onClose={() => setOpenSuggestions(false)}
              options={options}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.title || option.label || ""
              }
              filterOptions={(x) => x} // disable built-in filtering; backend provides filtered options
              onChange={(event, value) => {
                // value can be object or string
                if (!value) return;
                if (typeof value === "string") {
                  setQ(value);
                  search(value);
                } else {
                  const chosenText = value.title || value.label || "";
                  setQ(chosenText);
                  // immediately perform search on selected suggestion
                  search(chosenText);
                }
                setOpenSuggestions(false);
              }}
              inputValue={q}
              onInputChange={(event, newInputValue, reason) => {
                // reason can be 'input'|'reset'|'clear'
                if (reason === "reset") {
                  // when option selected, ignore
                  return;
                }
                setQ(newInputValue);
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id || (option.title || option.label)}>
                  <Box>
                    <div style={{ fontWeight: 600 }}>{option.title || option.label}</div>
                    {option.snippet && <div style={{ fontSize: 12, color: "#666" }}>{option.snippet}</div>}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  placeholder="Search Acts / Rules / Sections / Circulars / Judgements"
                  onKeyDown={handleKeyDown}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaSearch />
                      </InputAdornment>
                    )
                  }}
                />
              )}
            />
          </Grid>


          {/* Act */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Act</InputLabel>
              <Select
                label="Act"
                value={filters.act}
                onChange={(e) => setFilters({ ...filters, act: e.target.value })}
              >
                 <MenuItem value="All">All</MenuItem>
                {domainList.map((d) => (
                  <MenuItem key={d.code} value={d.name}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* State (conditional) */}
        
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>State</InputLabel>
              <Select
                label="State"
                value={filters.state}
                onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {stateList.map((s) => (
                  <MenuItem key={s.code} value={s.code}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

                    {/* Act */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={filters.categories}
                onChange={(e) => setFilters({ ...filters, categories: e.target.value })}
              >
                 <MenuItem value="All">All</MenuItem>
                {categoriesList.map((d) => (
                  <MenuItem key={d.code} value={d.name}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Search Button */}
          <Grid item xs={12} sm={6} md={"auto"}>
            <Button variant="contained" onClick={() => search()} disabled={loading}>
              {loading ? "Searching..." : "Search"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      
      {/* Results */}
      <Paper sx={{ p: 2, maxHeight: 500, overflowY: "auto" }}>

        {/* Header */}
        <Typography sx={{ mb: 2, fontSize: 14, color: "#6d6d6d" }}>
          {results.length > 0 ? `About ${results.length} results` : "No results yet"}
        </Typography>

        {/* Results Rendering */}
        {results.map((item, index) => (
          <Box
            key={item.id || index}
            sx={{
              mb: 3,
              pb: 2,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            {/* Clickable Title */}
            <Typography
              component="span"
              onClick={() => onResultClick(item)}
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#1a0dab",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {item.title || item.label || "Untitled Document"}
            </Typography>


            {/* Green URL Line */}
            <Typography
              sx={{
                fontSize: "14px",
                color: "#006621",
                mt: 0.5,
              }}
            >
              {item.s3_key?.substring(0, 80)}...
            </Typography>

            {/* Snippet */}
            <Typography
              sx={{
                mt: 1,
                fontSize: "15px",
                color: "#444",
                lineHeight: 1.4,
              }}
            >
              {item.text_en_full?.slice(0, 200)}...
            </Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default SearchPane;
